'use client';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import * as React from 'react';
import { getImageUrl } from '@/lib/cdn';
import { Button } from '@/components/ui/button';
import { usePostHog } from 'posthog-js/react';
import { BOOK_DEMO_HREF, SALES_DEMO_HREF } from '@/constants/publicConversionPaths';

type IFooterProps = object;

const Footer: React.FunctionComponent<IFooterProps> = () => {
  const posthog = usePostHog();
  const pathname = usePathname() ?? '';
  const isHeygen = pathname.includes('/heygen');
  const isDailySdrPlaybook = pathname.includes('/daily-sdr-playbook');
  const isSupport = pathname === '/support';

  const topCtaText = isHeygen
    ? 'Start your first MarketBetter × HeyGen campaign now'
    : isDailySdrPlaybook
      ? 'See the Daily SDR Playbook mapped to your workflow'
      : isSupport
        ? 'Need a hand with the MarketBetter app?'
        : 'See what MarketBetter could automate for your team';

  const topCtaButtonText = isHeygen ? 'Try now' : isSupport ? 'Email support' : 'Book a call';

  const topCtaHref = isHeygen
    ? '/sign-up'
    : isSupport
      ? 'mailto:support@marketbetter.ai?subject=MarketBetter%20iPhone%20app%20support'
      : BOOK_DEMO_HREF;

  return (
    <footer className='bg-[#05226C] border-t border-stroke'>
      <div className='border-b border-[#DFE4EA] py-[60px]'>
        <div className='container-fluid px-4 md:px-7.5 xl:px-25'>
          <div className='flex flex-row items-center justify-between gap-6'>
            <p className='text-sm sm:text-3xl xl:text-[40px] xl:leading-[82px] font-medium text-white max-w-[663px] flex-1 min-w-0'>
              {topCtaText}
            </p>
            <div className='flex shrink-0 flex-col items-center gap-3 sm:flex-row'>
              <Button
                className='shrink-0 bg-white !text-[#094FFE] hover:!text-white !rounded-[4px] !h-[50px] !px-8 !font-medium'
                linearOnHover
                href={topCtaHref}
                onClick={() => {
                  posthog?.capture(
                    isHeygen
                      ? 'nav_trial_clicked'
                      : isSupport
                        ? 'nav_support_email_clicked'
                        : 'nav_book_demo_clicked',
                    { source: 'footer' },
                  );
                }}
              >
                {topCtaButtonText}
              </Button>
              {!isHeygen && !isSupport && (
                <Link
                  href={SALES_DEMO_HREF}
                  className='text-sm font-medium text-white underline-offset-4 hover:underline'
                  onClick={(event) => {
                    posthog?.capture('nav_product_demo_clicked', { source: 'footer' });
                    if (pathname === '/') {
                      event.preventDefault();
                      window.history.pushState({}, '', SALES_DEMO_HREF);
                      window.dispatchEvent(
                        new CustomEvent('marketbetter:open-demo', { detail: 'sales' }),
                      );
                    }
                  }}
                >
                  Watch 4-minute demo →
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className='container-fluid px-4 md:px-7.5 xl:px-25'>
        {/* Footer Top */}
        <div className='py-12 lg:py-[100px]'>
          <div className='grid grid-cols-1 lg:grid-cols-[5fr_4fr_4fr] gap-8'>
            {/* Col 1: Logo + tagline + contact (desktop) */}
            <div>
              <Link href='/'>
                <Image
                  src={getImageUrl('logo/logo-footer.png')}
                  alt='MarketBetter logo'
                  width={582}
                  height={132}
                  className='h-auto w-[130px] lg:w-[194px]'
                />
              </Link>
              <p className='mt-2 text-white text-base leading-[26px]'>
                Customer acquisition without the cost.
              </p>
              <div className='hidden lg:block mt-8'>
                <p className='uppercase tracking-[5px] text-sm text-white mb-0.5'>contact</p>
                <a
                  href='mailto:sunder@marketbetter.ai'
                  className='text-sm leading-5 text-white hover:text-[#CBF8E7]'
                >
                  sunder@marketbetter.ai
                </a>
              </div>
            </div>

            {/* Col 2: Quick Links */}
            <div>
              <h4 className='text-itemtitle2 font-medium text-white leading-8 mb-9'>Quick Links</h4>
              <ul className='text-white text-base space-y-3'>
                <li>
                  {/* `#top` only resolves on /mcp, so everywhere else this scrolled
                      the current page instead of going home. */}
                  <Link href='/' className='hover:text-[#CBF8E7]'>
                    Home
                  </Link>
                </li>
                <li>
                  <Link href='/support' className='hover:text-[#CBF8E7]'>
                    Support
                  </Link>
                </li>
                <li>
                  <a href='mailto:press@marketbetter.ai' className='hover:text-[#CBF8E7]'>
                    Press media
                  </a>
                </li>
              </ul>
            </div>

            {/* Col 3: Subscribe + contact (mobile only) */}
            <div>
              <p className='text-base leading-[26px] text-white mb-2'>
                Subscribe to receive future updates
              </p>
              <form action='#' onSubmit={(e) => e.preventDefault()}>
                <div className='relative'>
                  <input
                    type='email'
                    placeholder='Email address'
                    className='w-full h-[41px] border-[0.5px] border-[#EFEFEF] bg-transparent text-white text-sm placeholder:text-[#ADADAD] placeholder:text-xs rounded-full focus:outline-none focus:border-white/50 px-4'
                  />
                  <button
                    type='submit'
                    className='absolute right-0 top-0 h-full w-11 flex items-center justify-end pr-3 text-white/70 hover:text-white transition-colors'
                    aria-label='Subscribe'
                  >
                    <svg width={14} height={14} viewBox='0 0 20 20' fill='currentColor' aria-hidden>
                      <path d='M3.1175 1.17318L18.5025 9.63484a1 1 0 010 1.7327L3.1175 18.8265A1 1 0 011.667 17.9615V11.667L10 10 1.667 8.333V2.0385a1 1 0 011.4505-.865z' />
                    </svg>
                  </button>
                </div>
              </form>
              {/* Contact — mobile only, appears after subscribe */}
              <div className='block lg:hidden mt-8'>
                <p className='uppercase tracking-[5px] text-sm text-white mb-0.5'>contact</p>
                <a
                  href='mailto:sunder@marketbetter.ai'
                  className='text-sm leading-5 text-white hover:text-[#CBF8E7]'
                >
                  sunder@marketbetter.ai
                </a>
              </div>
            </div>
          </div>
        </div>
        {/* Footer Top */}
      </div>
      {/* Footer Bottom */}
      <div className='container-fluid px-4 md:px-7.5 xl:px-25'>
        <div className='border-t border-[#DFE4EA]/40 flex flex-wrap flex-col lg:flex-row items-center justify-center lg:justify-between gap-5 lg:gap-0 py-7'>
          <div className='animate_top'>
            <ul className='flex items-center gap-8 text-white'>
              <li>
                {/* The site ships in English only; this was a link to `#`, which
                    looked clickable and jumped to the top of the page. */}
                <span>English</span>
              </li>
              <li>
                <a href='https://www.marketbetter.ai/terms' className='hover:text-primary'>
                  Terms
                </a>
              </li>
              <li>
                <a href='https://www.marketbetter.ai/privacy' className='hover:text-primary'>
                  Privacy Policy
                </a>
              </li>
            </ul>
          </div>
          <div className='animate_top'>
            <p className='text-white text-center'>© 2025 marketbetter.ai All rights reserved</p>
          </div>
        </div>
      </div>
      {/* Footer Bottom */}
    </footer>
  );
};

export default Footer;
